import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../services/api";

// Async Thunks for Bill CRUD operations

// Get all bills
export const getAllBills = createAsyncThunk(
  "bill/getAllBills",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/bills");
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch bills"
      );
    }
  }
);

// Get my bills (created by logged-in user)
export const getMyBills = createAsyncThunk(
  "bill/getMyBills",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/bills/my");
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch your bills"
      );
    }
  }
);

// Get bills by party
export const getBillsByParty = createAsyncThunk(
  "bill/getBillsByParty",
  async (partyId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/bills/party/${partyId}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch bills"
      );
    }
  }
);

// Get all parties (for dropdown)
export const getAllParties = createAsyncThunk(
  "bill/getAllParties",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/parties");
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch parties"
      );
    }
  }
);

// Get all processes (for dropdown)
export const getAllProcesses = createAsyncThunk(
  "bill/getAllProcesses",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/processes");
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch processes"
      );
    }
  }
);

// Get single bill by ID
export const getBillById = createAsyncThunk(
  "bill/getBillById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/bill/${id}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch bill"
      );
    }
  }
);

// Create new bill
export const createBill = createAsyncThunk(
  "bill/createBill",
  async (billData, { rejectWithValue }) => {
    try {
      const { data } = await api.post("/bill", billData);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 
        error.response?.data?.errors?.join(", ") || 
        error.message || 
        "Failed to create bill"
      );
    }
  }
);

// Update bill
export const updateBill = createAsyncThunk(
  "bill/updateBill",
  async ({ id, billData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/bill/${id}`, billData);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 
        error.response?.data?.errors?.join(", ") || 
        error.message || 
        "Failed to update bill"
      );
    }
  }
);

// Delete bill
export const deleteBill = createAsyncThunk(
  "bill/deleteBill",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/bill/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to delete bill"
      );
    }
  }
);

// Search bills
export const searchBills = createAsyncThunk(
  "bill/searchBills",
  async (searchQuery, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/bills/search?search=${searchQuery}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to search bills"
      );
    }
  }
);

// Get bills with pagination (optional my: 1 for logged-in user's bills)
export const getBillsWithPagination = createAsyncThunk(
  "bill/getBillsWithPagination",
  async ({ page = 1, limit = 20, my }, { getState, rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (my === 1) params.set("my", "1");
      const { data } = await api.get(`/bills/paginated?${params.toString()}`);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch bills"
      );
    }
  }
);

// Bill Slice
const billSlice = createSlice({
  name: "bill",
  initialState: {
    bills: [],
    parties: [],
    processes: [],
    currentBill: null,
    loading: false,
    error: null,
    success: false,
    message: null,
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
    },
  },
  reducers: {
    clearErrors: (state) => {
      state.error = null;
    },
    clearMessage: (state) => {
      state.message = null;
    },
    resetSuccess: (state) => {
      state.success = false;
    },
    setCurrentBill: (state, action) => {
      state.currentBill = action.payload;
    },
    clearCurrentBill: (state) => {
      state.currentBill = null;
    },
  },
  extraReducers: (builder) => {
    // Get all bills
    builder.addCase(getAllBills.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getAllBills.fulfilled, (state, action) => {
      state.loading = false;
      state.bills = action.payload;
      state.error = null;
    });
    builder.addCase(getAllBills.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Get my bills
    builder.addCase(getMyBills.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getMyBills.fulfilled, (state, action) => {
      state.loading = false;
      state.bills = action.payload;
      state.error = null;
    });
    builder.addCase(getMyBills.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Get bills by party
    builder.addCase(getBillsByParty.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getBillsByParty.fulfilled, (state, action) => {
      state.loading = false;
      state.bills = action.payload;
      state.error = null;
    });
    builder.addCase(getBillsByParty.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Get all parties
    builder.addCase(getAllParties.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getAllParties.fulfilled, (state, action) => {
      state.loading = false;
      state.parties = action.payload;
      state.error = null;
    });
    builder.addCase(getAllParties.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Get all processes
    builder.addCase(getAllProcesses.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getAllProcesses.fulfilled, (state, action) => {
      state.loading = false;
      state.processes = action.payload;
      state.error = null;
    });
    builder.addCase(getAllProcesses.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Get bill by ID
    builder.addCase(getBillById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getBillById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentBill = action.payload;
      state.error = null;
    });
    builder.addCase(getBillById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Create bill
    builder.addCase(createBill.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(createBill.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
      state.bills.unshift(action.payload);
      state.message = "Bill created successfully";
      state.error = null;
    });
    builder.addCase(createBill.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.success = false;
    });

    // Update bill
    builder.addCase(updateBill.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(updateBill.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
      state.message = "Bill updated successfully";
      state.bills = state.bills.map((bill) =>
        bill._id === action.payload._id ? action.payload : bill
      );
      if (state.currentBill?._id === action.payload._id) {
        state.currentBill = action.payload;
      }
      state.error = null;
    });
    builder.addCase(updateBill.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.success = false;
    });

    // Delete bill
    builder.addCase(deleteBill.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(deleteBill.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
      state.message = "Bill deleted successfully";
      state.bills = state.bills.filter(
        (bill) => bill._id !== action.payload
      );
      if (state.currentBill?._id === action.payload) {
        state.currentBill = null;
      }
      state.error = null;
    });
    builder.addCase(deleteBill.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.success = false;
    });

    // Search bills
    builder.addCase(searchBills.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(searchBills.fulfilled, (state, action) => {
      state.loading = false;
      state.bills = action.payload;
      state.error = null;
    });
    builder.addCase(searchBills.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Get bills with pagination
    builder.addCase(getBillsWithPagination.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getBillsWithPagination.fulfilled, (state, action) => {
      state.loading = false;
      state.bills = action.payload.data;
      state.pagination = {
        page: action.payload.page,
        limit: action.payload.limit || 10,
        total: action.payload.total,
        totalPages: action.payload.totalPages,
      };
      state.error = null;
    });
    builder.addCase(getBillsWithPagination.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
  },
});

export const {
  clearErrors,
  clearMessage,
  resetSuccess,
  setCurrentBill,
  clearCurrentBill,
} = billSlice.actions;

export default billSlice.reducer;

